import { useState, useEffect, useCallback } from 'react';
import { api, extractErrorMessage } from '../api/client';
import { useToast } from '../components/Toast';
import { Button } from '../components/Button';

// ─── Types ────────────────────────────────────────────────────────────────

interface Role {
  id: number;
  nombre: string;
  descripcion: string | null;
  esSistema: boolean;
  _count: { permisos: number; users: number };
}

interface RoleDetail {
  id: number;
  nombre: string;
  descripcion: string | null;
  esSistema: boolean;
  permisos: RolePermiso[];
  _count: { users: number };
}

interface RolePermiso {
  id: number;
  moduloId: number;
  moduloNombre: string;
  accionId: number;
  accionNombre: string;
}

interface Modulo {
  id: number;
  nombre: string;
  descripcion: string | null;
}

interface Accion {
  id: number;
  nombre: string;
}

interface Dependencia {
  id: number;
  moduloOrigenNombre: string;
  accionOrigenNombre: string;
  moduloDestinoNombre: string;
}

type Tab = 'matriz' | 'dependencias';

// ─── Component ────────────────────────────────────────────────────────────

export function PermisosPage() {
  const toast = useToast();

  // Tab state
  const [activeTab, setActiveTab] = useState<Tab>('matriz');

  // Data
  const [roles, setRoles] = useState<Role[]>([]);
  const [modulos, setModulos] = useState<Modulo[]>([]);
  const [acciones, setAcciones] = useState<Accion[]>([]);
  const [selectedRoleId, setSelectedRoleId] = useState<number | null>(null);

  // Matrix state: `${moduloId}-${accionId}` → checked
  const [matrix, setMatrix] = useState<Record<string, boolean>>({});

  // UI state
  const [loadingRoles, setLoadingRoles] = useState(true);
  const [loadingMatrix, setLoadingMatrix] = useState(false);
  const [saving, setSaving] = useState(false);

  // ── Fetch initial data (roles, modulos, acciones) ──────────────────────

  useEffect(() => {
    let cancelled = false;

    async function loadInitial() {
      try {
        const [rolesRes, modulosRes, accionesRes] = await Promise.all([
          api.get<Role[]>('/api/v3/permisos/roles'),
          api.get<Modulo[]>('/api/v3/permisos/modulos'),
          api.get<Accion[]>('/api/v3/permisos/acciones'),
        ]);
        if (cancelled) return;
        setRoles(rolesRes);
        setModulos(modulosRes);
        setAcciones(accionesRes);
      } catch (e) {
        if (!cancelled) toast.addToast(extractErrorMessage(e), 'error');
      } finally {
        if (!cancelled) setLoadingRoles(false);
      }
    }

    loadInitial();
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Load role details when selection changes ───────────────────────────

  const loadRole = useCallback(async (roleId: number) => {
    setLoadingMatrix(true);
    try {
      const role = await api.get<RoleDetail>(`/api/v3/permisos/roles/${roleId}`);

      // Build matrix from current permisos
      const newMatrix: Record<string, boolean> = {};
      for (const mod of modulos) {
        for (const acc of acciones) {
          newMatrix[`${mod.id}-${acc.id}`] = role.permisos.some(
            (p) => p.moduloId === mod.id && p.accionId === acc.id,
          );
        }
      }
      setMatrix(newMatrix);
    } catch (e) {
      toast.addToast(extractErrorMessage(e), 'error');
    } finally {
      setLoadingMatrix(false);
    }
  }, [modulos, acciones, toast]);

  // ── Role change handler ────────────────────────────────────────────────

  const handleRoleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const id = parseInt(e.target.value, 10);
    if (isNaN(id)) {
      setSelectedRoleId(null);
      setMatrix({});
      return;
    }
    setSelectedRoleId(id);
    loadRole(id);
  };

  // ── Checkbox toggle ────────────────────────────────────────────────────

  const togglePermiso = (moduloId: number, accionId: number) => {
    const key = `${moduloId}-${accionId}`;
    setMatrix((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const toggleRow = (moduloId: number) => {
    const rowKeys = acciones.map((a) => `${moduloId}-${a.id}`);
    const allChecked = rowKeys.every((k) => matrix[k]);
    setMatrix((prev) => {
      const next = { ...prev };
      rowKeys.forEach((k) => { next[k] = !allChecked; });
      return next;
    });
  };

  const setAll = (value: boolean) => {
    setMatrix((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((k) => { next[k] = value; });
      return next;
    });
  };

  // ── Save ───────────────────────────────────────────────────────────────

  const handleSave = async () => {
    if (!selectedRoleId) return;

    const permisos = Object.entries(matrix)
      .filter(([, checked]) => checked)
      .map(([key]) => {
        const [moduloId, accionId] = key.split('-').map(Number);
        return { moduloId, accionId };
      });

    setSaving(true);
    try {
      await api.patch(`/api/v3/permisos/roles/${selectedRoleId}/permisos`, { permisos });
      toast.addToast('Permisos guardados correctamente', 'success');
      // Reload to get fresh data with new IDs
      loadRole(selectedRoleId);
    } catch (e) {
      toast.addToast(extractErrorMessage(e), 'error');
    } finally {
      setSaving(false);
    }
  };

  // ── Accion label lookup ────────────────────────────────────────────────

  const accionLabels: Record<string, string> = {
    ver: 'Ver',
    crear: 'Crear',
    editar: 'Editar',
    eliminar: 'Eliminar',
  };

  // ── Render ─────────────────────────────────────────────────────────────

  const selectedRoleData = roles.find((r) => r.id === selectedRoleId);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-lg font-semibold text-slate-800">Permisos</h2>
        <p className="text-sm text-slate-500 mt-1">
          Configurá permisos de rol y dependencias implícitas del sistema.
        </p>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-slate-200">
        <button
          onClick={() => setActiveTab('matriz')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'matriz'
              ? 'border-brand-600 text-brand-600'
              : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}
        >
          Matriz de Permisos
        </button>
        <button
          onClick={() => setActiveTab('dependencias')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'dependencias'
              ? 'border-brand-600 text-brand-600'
              : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}
        >
          Dependencias Implícitas
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === 'matriz' ? (
        <>
          {/* Role selector + Save */}
          <div className="bg-white rounded-lg border border-slate-200 p-4">
            <div className="flex flex-wrap items-end gap-4">
              <div className="flex-1 min-w-[200px]">
                <label className="block text-xs font-medium text-slate-600 mb-1">
                  Seleccionar Rol
                </label>
                <select
                  className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                  value={selectedRoleId ?? ''}
                  onChange={handleRoleChange}
                  disabled={loadingRoles}
                >
                  <option value="">-- Seleccionar rol --</option>
                  {roles.map((role) => (
                    <option key={role.id} value={role.id}>
                      {role.nombre}
                      {role.esSistema ? ' (sistema)' : ''}
                    </option>
                  ))}
                </select>
              </div>

              {selectedRoleData && (
                <div className="text-sm text-slate-500">
                  <span className="font-medium text-slate-700">
                    {selectedRoleData._count.permisos}
                  </span>{' '}
                  permisos asignados
                  {selectedRoleData._count.users > 0 && (
                    <> ·{' '}
                      <span className="font-medium text-slate-700">
                        {selectedRoleData._count.users}
                      </span>{' '}
                      usuarios
                    </>
                  )}
                </div>
              )}

              {selectedRoleId && (
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setAll(true)}
                    className="text-xs px-2 py-1 rounded border border-slate-300 text-slate-600 hover:bg-slate-100 transition-colors"
                  >
                    ✓ Todo
                  </button>
                  <button
                    onClick={() => setAll(false)}
                    className="text-xs px-2 py-1 rounded border border-slate-300 text-slate-600 hover:bg-slate-100 transition-colors"
                  >
                    ✗ Nada
                  </button>
                  <Button onClick={handleSave} loading={saving}>
                    {saving ? 'Guardando...' : 'Guardar Cambios'}
                  </Button>
                </div>
              )}
            </div>
          </div>

          {/* Matrix */}
          {selectedRoleId && (
            <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
              {loadingMatrix ? (
                <div className="p-8 text-center text-sm text-slate-400" role="status">
                  Cargando permisos...
                </div>
              ) : modulos.length === 0 ? (
                <div className="p-8 text-center text-sm text-slate-400">
                  No hay módulos configurados.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-slate-50 border-b border-slate-200">
                        <th className="text-left px-4 py-3 font-medium text-slate-600 sticky left-0 bg-slate-50 z-10 min-w-[180px]">
                          Módulo
                        </th>
                        {acciones.map((accion) => (
                          <th
                            key={accion.id}
                            className="text-center px-3 py-3 font-medium text-slate-600 w-24"
                          >
                            {accionLabels[accion.nombre] ?? accion.nombre}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {modulos.map((modulo, idx) => {
                        const rowKeys = acciones.map((a) => `${modulo.id}-${a.id}`);
                        const checkedCount = rowKeys.filter((k) => matrix[k]).length;
                        const rowAll = checkedCount === acciones.length;
                        const rowSome = checkedCount > 0 && !rowAll;
                        return (
                        <tr
                          key={modulo.id}
                          className={`border-b border-slate-100 ${
                            idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'
                          } hover:bg-slate-50 transition-colors`}
                        >
                          <td className="px-4 py-2.5 sticky left-0 bg-inherit z-[5]">
                            <button
                              onClick={() => toggleRow(modulo.id)}
                              className="flex items-center gap-2 text-left w-full group"
                              title={rowAll ? 'Desmarcar módulo' : 'Marcar módulo'}
                            >
                              <span className={`font-medium ${rowAll ? 'text-brand-700' : rowSome ? 'text-slate-700' : 'text-slate-400'}`}>
                                {modulo.nombre}
                              </span>
                              {rowSome && <span className="text-[10px] text-amber-500 font-semibold">({checkedCount}/{acciones.length})</span>}
                            </button>
                          </td>
                          {acciones.map((accion) => {
                            const key = `${modulo.id}-${accion.id}`;
                            const checked = matrix[key] ?? false;
                            return (
                              <td key={key} className="text-center px-3 py-2.5">
                                <label className="flex items-center justify-center w-full h-full py-2 cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={() => togglePermiso(modulo.id, accion.id)}
                                    className="w-4 h-4 text-brand-600 border-slate-300 rounded focus:ring-brand-500 cursor-pointer"
                                  />
                                </label>
                              </td>
                            );
                          })}
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </>
      ) : (
        <DependenciasTab modulos={modulos} toast={toast} />
      )}
    </div>
  );
}

// ─── DependenciasTab (inline) ──────────────────────────────────────────────

function DependenciasTab({
  modulos,
  toast,
}: {
  modulos: Modulo[];
  toast: ReturnType<typeof useToast>;
}) {
  const [deps, setDeps] = useState<Dependencia[]>([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);

  // Add form state
  const [formOrigen, setFormOrigen] = useState('');
  const [formAccion, setFormAccion] = useState('crear');
  const [formDestino, setFormDestino] = useState('');

  const loadDeps = useCallback(async () => {
    try {
      const data = await api.get<Dependencia[]>('/api/v3/permisos/dependencias');
      setDeps(data);
    } catch (e) {
      toast.addToast(extractErrorMessage(e), 'error');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    loadDeps();
  }, [loadDeps]);

  // Group by trigger
  const grouped: Record<string, Dependencia[]> = {};
  for (const d of deps) {
    const key = `${d.moduloOrigenNombre}:${d.accionOrigenNombre}`;
    (grouped[key] ??= []).push(d);
  }

  const handleAdd = async () => {
    if (!formOrigen || !formDestino) return;
    setAdding(true);
    try {
      await api.post('/api/v3/permisos/dependencias', {
        moduloOrigenId: Number(formOrigen),
        accionOrigenId: formAccion === 'crear' ? 2 : 3,
        moduloDestinoId: Number(formDestino),
      });
      toast.addToast('Dependencia creada', 'success');
      setFormOrigen('');
      setFormDestino('');
      loadDeps();
    } catch (e) {
      toast.addToast(extractErrorMessage(e), 'error');
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await api.delete(`/api/v3/permisos/dependencias/${id}`);
      toast.addToast('Dependencia eliminada', 'success');
      loadDeps();
    } catch (e) {
      toast.addToast(extractErrorMessage(e), 'error');
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-slate-200 p-8 text-center text-sm text-slate-400" role="status">
        Cargando dependencias...
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Add form */}
      <div className="bg-white rounded-lg border border-slate-200 p-4">
        <h3 className="text-sm font-medium text-slate-700 mb-3">Agregar dependencia</h3>
        <p className="text-xs text-slate-500 mb-3">
          Cuando un rol tiene <strong>modulo:accion</strong>, recibe automáticamente{' '}
          <strong>destino:ver</strong>.
        </p>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs text-slate-600 mb-1">Módulo origen</label>
            <select
              className="border border-slate-300 rounded-md px-3 py-1.5 text-sm"
              value={formOrigen}
              onChange={(e) => setFormOrigen(e.target.value)}
            >
              <option value="">--</option>
              {modulos.map((m) => (
                <option key={m.id} value={m.id}>{m.nombre}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-slate-600 mb-1">Acción origen</label>
            <select
              className="border border-slate-300 rounded-md px-3 py-1.5 text-sm"
              value={formAccion}
              onChange={(e) => setFormAccion(e.target.value)}
            >
              <option value="crear">crear</option>
              <option value="editar">editar</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-slate-600 mb-1">Módulo destino</label>
            <select
              className="border border-slate-300 rounded-md px-3 py-1.5 text-sm"
              value={formDestino}
              onChange={(e) => setFormDestino(e.target.value)}
            >
              <option value="">--</option>
              {modulos.map((m) => (
                <option key={m.id} value={m.id}>{m.nombre}</option>
              ))}
            </select>
          </div>
          <Button size="sm" onClick={handleAdd} disabled={!formOrigen || !formDestino} loading={adding}>
            {adding ? 'Agregando...' : 'Agregar'}
          </Button>
        </div>
      </div>

      {/* Rules list */}
      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        {deps.length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-400">
            No hay reglas de dependencia configuradas.
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {Object.entries(grouped).map(([trigger, rules]) => (
              <div key={trigger} className="px-4 py-3">
                <div className="text-sm font-medium text-slate-700 mb-1">
                  {trigger} <span className="text-slate-400">→</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {rules.map((r) => (
                    <span
                      key={r.id}
                      className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-100 text-slate-600 text-xs rounded"
                    >
                      {r.moduloDestinoNombre}:ver
                      <button
                        onClick={() => handleDelete(r.id)}
                        className="text-slate-400 hover:text-red-500 transition-colors"
                        title="Eliminar"
                        aria-label="Eliminar"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
